import { WalkModeRenderOrders } from "@/modes/walk-mode/walk-mode-render-orders";
import { isPerspectiveCamera } from "@/types/threejs-type-guards";
import { DEFAULT_PERSPECTIVE_FOV } from "@faro-lotv/lotv";
import type { BoundingBoxCoordinates } from "@faro-lotv/service-wires";
import { useLotvDispose } from "@faro-lotv/spatial-ui";
import { useFrame } from "@react-three/fiber";
import { useMemo, type JSX, type Ref } from "react";
import { DoubleSide, Mesh, type Texture } from "three";
import { boundingBoxToSphereGeometry } from "./bounding-box-geometry";
import { BoundingBoxRendererMaterial } from "./bounding-box-renderer-material";

type BoundingBoxMeshProps = {
  /** The normalized [0,1] bounding box coordinates. */
  boundingBox: BoundingBoxCoordinates;

  /** The AI gradient to paint the border of the box with. */
  gradient: Texture;

  /** Optional ref to the underlying mesh, e.g. to capture a screenshot of it. */
  meshRef?: Ref<Mesh>;
};

/**
 * @returns the semi-transparent mesh overlay for a single bounding box, rendered
 * on the surface of the unit sphere using spherical coordinates.
 */
export function BoundingBoxMesh({
  boundingBox,
  gradient,
  meshRef,
}: BoundingBoxMeshProps): JSX.Element {
  const boundingBoxMesh = useMemo(() => {
    const geometry = boundingBoxToSphereGeometry(boundingBox);

    // Create a semi-transparent mesh for the bounding box overlay
    const mesh = new Mesh(
      geometry,
      new BoundingBoxRendererMaterial({
        side: DoubleSide,
        transparent: true,
        gradient,
        xSize: Math.abs(boundingBox.x2 - boundingBox.x1),
        ySize: Math.abs(boundingBox.y2 - boundingBox.y1),
      }),
    );

    // Ensure the bounding box renders on top of other objects
    mesh.renderOrder = WalkModeRenderOrders.AiBoundingBoxes;

    return mesh;
  }, [boundingBox, gradient]);

  useLotvDispose(boundingBoxMesh);

  // Keep the border thickness constant on screen by scaling it with the camera
  // FOV: zooming into the pano shrinks the FOV, which would otherwise make the
  // fixed angular border cover more pixels and hide the underlying data.
  useFrame(({ camera }) => {
    const fov = isPerspectiveCamera(camera)
      ? camera.getEffectiveFOV()
      : DEFAULT_PERSPECTIVE_FOV;
    boundingBoxMesh.material.setZoomScale(fov);
  });

  return <primitive object={boundingBoxMesh} ref={meshRef} />;
}
